import React, { useState, useEffect, useContext, useMemo } from 'react';
import { FiPlus, FiEye, FiTrash2, FiEdit, FiUsers, FiSearch, FiDownload } from 'react-icons/fi';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import AuthContext from '../context/AuthContext';
import ClientModal from '../components/AddClientModal';
import Spinner from '../components/Spinner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ConfirmModal from '../components/ConfirmModal';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const { user, openUpgradeModal } = useContext(AuthContext);
  const navigate = useNavigate();
  const isProOrPremium = user?.plan === 'pro' || user?.plan === 'premium';

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error("Impossible de charger les clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSaveClient = async (clientData, clientId) => {
    if (clientId) {
      // Mode édition
      try {
        await api.put(`/clients/${clientId}`, clientData);
        toast.success(`Client "${clientData.name}" modifié avec succès !`);
        fetchClients();
      } catch (error) {
        toast.error("Erreur lors de la modification du client.");
      }
    } else {
      // Mode ajout
      try {
        await api.post('/clients', clientData);
        toast.success(`Client "${clientData.name}" ajouté avec succès !`);
        fetchClients();
      } catch (error) {
        toast.error("Erreur lors de l'ajout du client.");
      }
    }
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleDeleteRequest = (client) => {
    setClientToDelete(client);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;
    try {
      await api.delete(`/clients/${clientToDelete._id}`);
      toast.success("Client supprimé avec succès.");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de la suppression du client.");
    } finally {
      setIsConfirmModalOpen(false);
      setClientToDelete(null);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const filteredClients = useMemo(() => {
    return clients.filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.phone && client.phone.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [clients, searchTerm]);

  const handleExportPDF = () => {
    if (!isProOrPremium) {
      toast.info("Exportez votre liste de clients en PDF. Passez au plan Pro pour débloquer cette fonctionnalité.", { icon: "📄" });
      openUpgradeModal();
      return;
    }
    const doc = new jsPDF();
    
    // En-tête du document
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(user?.shopName || 'Jaapand-e', 14, 22);

    doc.setFontSize(12);
    doc.text("Liste des Clients", 14, 32);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Généré le: ${new Date().toLocaleString('fr-FR')}`, 14, 42);

    autoTable(doc, {
      startY: 50,
      head: [['Nom', 'Téléphone', 'Total Dépensé (FCFA)', 'Dernier Achat']],
      body: filteredClients.map(c => [
        c.name, 
        c.phone || '-',
        (c.totalSpent || 0).toLocaleString('fr-FR').replace(/\s/g, "."),
        c.lastPurchaseDate ? new Date(c.lastPurchaseDate).toLocaleDateString('fr-FR') : 'N/A'
      ]),
      headStyles: { fillColor: '#2196F3' }, // brand-blue
      theme: 'grid',
    });
    doc.save('clients_jaapande.pdf');
  };

  const handleExportExcel = () => {
    if (!isProOrPremium) {
      toast.info("Exportez et analysez vos données clients sur Excel. Passez au plan Pro pour débloquer cette fonctionnalité.", { icon: "📊" });
      openUpgradeModal();
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(
      filteredClients.map(c => ({ 
        'Nom du Client': c.name, 
        'Téléphone': c.phone || '-',
        'Total Dépensé (CFA)': c.totalSpent || 0,
        'Dernier Achat': c.lastPurchaseDate ? new Date(c.lastPurchaseDate).toLocaleDateString('fr-FR') : 'N/A',
        'Date d\'ajout': new Date(c.createdAt).toLocaleDateString('fr-FR')
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");

    // Ajuster la largeur des colonnes
    worksheet['!cols'] = [ // wch = width in characters
      { wch: 30 }, // Nom du Client
      { wch: 20 }, // Téléphone
      { wch: 20 }, // Total Dépensé (CFA)
      { wch: 15 }, // Dernier Achat
      { wch: 15 }, // Date d'ajout
    ];

    XLSX.writeFile(workbook, "clients_jaapande.xlsx");
  };

  if (loading) {
    return <Spinner message="Chargement des clients..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Clients</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-3 justify-start">
            <button onClick={handleExportPDF} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled={!isProOrPremium} title={!isProOrPremium ? "Fonctionnalité Premium" : "Exporter en PDF"}><FiDownload className="mr-2" /><span>PDF</span></button>
            <button onClick={handleExportExcel} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled={!isProOrPremium} title={!isProOrPremium ? "Fonctionnalité Premium" : "Exporter en Excel"}><FiDownload className="mr-2" /><span>Excel</span></button>
          </div>
          <button onClick={() => { setEditingClient(null); setIsModalOpen(true); }} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors duration-200">
            <FiPlus className="mr-2" />
            <span>Ajouter un client</span>
          </button>
        </div>
      </div>

      {/* Filtres et Résumé */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white p-4 rounded-lg shadow-md flex items-center">
          <div className="relative w-full">
            <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un client par nom ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-100 border-2 border-gray-200 rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:border-brand-blue"
            />
          </div>
        </div>
        <div className="bg-blue-500 text-white p-4 rounded-lg shadow-lg flex items-center">
          <FiUsers className="h-10 w-10 mr-4" />
          <div>
            <p className="text-sm font-medium">Nombre de Clients</p>
            <p className="text-2xl font-bold">{clients.length}</p>
          </div>
        </div>
      </div>

      {/* Vue Mobile (Cartes) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {filteredClients.map((client) => (
          <div key={client._id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-all duration-200">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-gray-800">{client.name}</h3>
                <Link
                  to={`${client._id}`}
                  className={`text-gray-400 ${isProOrPremium ? 'hover:text-brand-blue' : 'opacity-50 cursor-not-allowed'}`}
                  onClick={(e) => { if (!isProOrPremium) { e.preventDefault(); toast.info("Analysez le comportement de vos clients ! Passez au plan Pro pour voir le détail de leurs achats et dettes.", { icon: "🚀" }); openUpgradeModal(); } }}
                  title={isProOrPremium ? "Voir le détail du client" : "Fonctionnalité Pro"}
                >
                  <FiEye className="w-5 h-5" />
                </Link>
              </div>
              <p className="text-sm text-gray-500 mb-3">{client.phone || 'Pas de téléphone'}</p>
              <div className="text-xs text-gray-600">
                <p>Total dépensé: <span className="font-semibold text-gray-800">{(client.totalSpent || 0).toLocaleString('fr-FR')} CFA</span></p>
                <p>Dernier achat: <span className="font-semibold text-gray-800">{client.lastPurchaseDate ? new Date(client.lastPurchaseDate).toLocaleDateString('fr-FR') : 'N/A'}</span></p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t pt-3 mt-3">
              <button onClick={() => handleEditClient(client)} className="text-gray-500 hover:text-brand-blue p-2 bg-gray-100 rounded-full" title="Modifier">
                <FiEdit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDeleteRequest(client)} className="text-gray-500 hover:text-red-500 p-2 bg-gray-100 rounded-full" title="Supprimer">
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-2xl overflow-hidden hidden md:block border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-5 py-3">Nom</th>
                <th className="px-5 py-3">Téléphone</th>
                <th className="px-5 py-3 text-right">Total Dépensé</th>
                <th className="px-5 py-3 text-center">Dernier Achat</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length > 0 ? filteredClients.map((client) => (
                <tr key={client._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{client.name}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-600 whitespace-no-wrap">{client.phone || '-'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-right">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{(client.totalSpent || 0).toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-center">
                    <p className="text-gray-600 whitespace-no-wrap">{client.lastPurchaseDate ? new Date(client.lastPurchaseDate).toLocaleDateString('fr-FR') : 'N/A'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`${client._id}`}
                        className={`text-gray-500 ${isProOrPremium ? 'hover:text-brand-blue' : 'opacity-50 cursor-not-allowed'}`}
                        onClick={(e) => { if (!isProOrPremium) { e.preventDefault(); toast.info("Analysez le comportement de vos clients ! Passez au plan Pro pour voir le détail de leurs achats et dettes.", { icon: "🚀" }); openUpgradeModal(); } }}
                        title={isProOrPremium ? "Voir le détail du client" : "Fonctionnalité Pro"}
                      >
                        <FiEye className="w-5 h-5" />
                      </Link>
                      <button onClick={() => handleEditClient(client)} className="text-gray-500 hover:text-brand-blue" title="Modifier le client">
                        <FiEdit className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDeleteRequest(client)} className="text-red-500 hover:text-red-700" title="Supprimer le client"><FiTrash2 className="w-5 h-5" /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="text-center py-10 text-gray-500">Aucun client trouvé.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ClientModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingClient(null); }} onSave={handleSaveClient} client={editingClient} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer le client"
        message={`Êtes-vous sûr de vouloir supprimer "${clientToDelete?.name}" ? Cette action est irréversible.`}
      />
    </div>
  );
};

export default Clients;